import { Configuration, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { config } from './config';

// MSAL configuration for Entra ID (Azure AD) authentication.
export const msalConfig: Configuration = {
    auth: {
        authority: config.REACT_APP_OAUTH_AUTHORITY,
        clientId: config.REACT_APP_OAUTH_CLIENT_ID,
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
    },
    cache: {
        cacheLocation: 'localStorage',
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    default:
                        return;
                }
            },
        },
    },
};

// Scopes requested when signing the user in. Using the backend's ".default"
// scope so a single acquisition also grants an access token whose audience
// the backend accepts and which includes all of the delegated permissions
// already consented for the backend API on the frontend app registration
// (e.g. Structures.Read). Configured via REACT_APP_OAUTH_SCOPE_BACKEND_API.
export const loginRequest = {
    scopes: [config.REACT_APP_OAUTH_SCOPE_BACKEND_API],
};

export const msalInstance = new PublicClientApplication(msalConfig);

// Acquires an access token for the backend API, silently if possible,
// falling back to an interactive redirect if silent acquisition fails
// (e.g. no cached session, consent required, etc.). In the redirect case
// the browser navigates away, so the promise never resolves on this page.
export async function getApiAccessToken(): Promise<string> {
    const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
    if (!account) {
        await msalInstance.acquireTokenRedirect(loginRequest);
        return '';
    }

    try {
        const result = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
        return result.accessToken;
    } catch (error) {
        await msalInstance.acquireTokenRedirect({ ...loginRequest, account });
        return '';
    }
}
